import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, Pagination } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { CME_KHOA_DAO_TAO_API, ACCOUNT_AUTHINFO_API } from '../../../../utils/apiUrl';
import { Get } from '../../../../utils/httpProvider';
import moment from 'moment';
import CMEParticipantCell from '../duyetHoSo/CMEParticipantCell';

const CMEEventParticipantModal = ({ isOpen, handleClose, eventData }) => {
    const { data, isLoading } = useQuery(
        ['cmeParticipants', eventData?.id],
        () => Get(`${CME_KHOA_DAO_TAO_API.GET_PARTICIPANTS}/${eventData?.id}`),
        { enabled: !!eventData?.id && isOpen }
    );

    const participants = data?.$values || data || [];

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchResults, setSearchResults] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const pageSize = 10;

    useEffect(() => {
        if (!searchTerm || searchTerm.length < 2) {
            setSearchResults(null);
            setIsSearching(false);
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            setIsSearching(true);
            try {
                const res = await Get(`${ACCOUNT_AUTHINFO_API.GET_PAGING}?Keyword=${searchTerm}&PageIndex=1&PageSize=100`);
                const items = res?.data || res?.items || res?.$values || [];
                const matchedIds = items.map(u => u.accountId || u.AccountId || u.id);
                setSearchResults(matchedIds);
            } catch (error) {
                console.error("Search identity failed:", error);
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const filteredParticipants = participants.filter(item => {
        if (!searchTerm) return true;
        
        if (searchResults !== null) {
            return searchResults.includes(item.userId);
        }

        return item.userId?.toLowerCase().includes(searchTerm.toLowerCase());
    });

    const totalPages = Math.ceil(filteredParticipants.length / pageSize);
    const paginatedItems = filteredParticipants.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <Modal show={isOpen} onHide={handleClose} size="xl" backdrop="static" style={{ zIndex: '9999' }}>
            <Modal.Header closeButton className="bg-info text-white">
                <Modal.Title>
                    Danh sách nhân viên đã cấp điểm - {eventData?.tenKhoaDaoTao}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row mb-3">
                    <div className="col-md-6">
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <span className="input-group-text bg-white border-right-0">
                                    {isSearching ? (
                                        <div className="spinner-border spinner-border-sm text-muted" role="status"></div>
                                    ) : (
                                        <i className="fas fa-search text-muted"></i>
                                    )}
                                </span>
                            </div>
                            <input
                                type="text"
                                className="form-control border-left-0"
                                placeholder="Tìm kiếm theo Họ tên hoặc CCCD"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-6 text-right pt-2">
                        <small className="text-muted font-italic">Đang hiển thị {paginatedItems.length} / {filteredParticipants.length} nhân viên</small>
                    </div>
                </div>

                <div className="table-responsive" style={{ minHeight: '300px' }}>
                    <Table striped bordered hover size="sm">
                        <thead className="bg-light">
                            <tr>
                                <th className="text-center" style={{ width: '50px' }}>STT</th>
                                <th>CCCD</th>
                                <th>Họ tên</th>
                                <th>Vai trò</th>
                                <th className="text-center" style={{ width: '80px' }}>Số điểm</th>
                                <th className="text-center" style={{ width: '150px' }}>Ngày cấp</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr><td colSpan={6} className="text-center py-4">Đang tải dữ liệu...</td></tr>
                            ) : paginatedItems.length === 0 ? (
                                <tr><td colSpan={6} className="text-center py-4 text-muted">Không tìm thấy nhân viên nào phù hợp.</td></tr>
                            ) : (
                                paginatedItems.map((item, idx) => (
                                    <tr key={item.id}>
                                        <td className="text-center">{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td>
                                            <CMEParticipantCell userId={item.userId} mode="username" />
                                        </td>
                                        <td>
                                            <CMEParticipantCell userId={item.userId} mode="name" />
                                        </td>
                                        <td>{item.vaiTro}</td>
                                        <td className="text-center font-weight-bold text-primary">{item.points}</td>
                                        <td className="text-center text-muted">
                                            {item.createdDate ? moment(item.createdDate).format('DD/MM/YYYY HH:mm') : '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-center mt-3">
                        <Pagination>
                            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                            <Pagination.Prev onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1} />

                            {[...Array(totalPages).keys()].slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2)).map(page => (
                                <Pagination.Item
                                    key={page + 1}
                                    active={page + 1 === currentPage}
                                    onClick={() => setCurrentPage(page + 1)}
                                >
                                    {page + 1}
                                </Pagination.Item>
                            ))}

                            <Pagination.Next onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages} />
                            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Đóng</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CMEEventParticipantModal;
